import type { ButtonHTMLAttributes, ReactNode } from "react";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  stretched?: boolean;
  children: ReactNode;
};

const variants: Record<Variant, string> = {
  primary:
    "bg-brand-purple text-white hover:bg-brand-purple/90 active:bg-brand-purple/95 disabled:bg-ink-200 disabled:text-ink-400",
  secondary:
    "bg-ink-100 text-ink-900 hover:bg-ink-200 active:bg-ink-200 disabled:bg-ink-100 disabled:text-ink-400",
  ghost:
    "bg-transparent text-brand-purple hover:bg-brand-purple-soft active:bg-brand-purple-soft disabled:text-ink-400",
  danger:
    "bg-brand-red text-white hover:bg-brand-red/90 active:bg-brand-red/95 disabled:bg-ink-200 disabled:text-ink-400",
};

const sizes: Record<Size, string> = {
  sm: "h-9 px-4 text-sm rounded-[var(--radius-sm)]",
  md: "h-11 px-5 text-[15px] rounded-[var(--radius-md)]",
  lg: "h-13 px-6 text-base rounded-[var(--radius-md)]",
};

export function Button({
  variant = "primary",
  size = "md",
  stretched,
  className = "",
  children,
  ...rest
}: Props) {
  return (
    <button
      type="button"
      className={`
        inline-flex items-center justify-center gap-2 font-semibold
        select-none cursor-pointer transition-all duration-150
        active:scale-[0.98] disabled:cursor-not-allowed disabled:scale-100
        ${variants[variant]} ${sizes[size]}
        ${stretched ? "w-full" : ""}
        ${className}
      `}
      {...rest}
    >
      {children}
    </button>
  );
}
